<template>
    <div class="meta-template-selector">
        <!-- Template Selection -->
        <div class="template-selection-section">
            <div class="section-title">{{ $t("whatsapp_business_api.selectATemplate") }}</div>
            <a-select
                v-model="selectedTemplateId"
                style="width: 100%"
                placeholder="Select a template"
                size="large"
            >
                <a-select-option v-for="template in templates" :key="template._id" :value="template._id">
                    {{ template.name }}
                </a-select-option>
            </a-select>
        </div>

        <template v-if="selectedTemplate">
            <a-divider />

            <!-- Template Configuration -->
            <div class="template-config-section">
                <div class="config-layout">
                    <!-- Left: Configuration -->
                    <div class="config-area">
                        <!-- Template Meta -->
                        <div class="meta-block">
                            <div class="meta-item">
                                <span class="meta-label">Template</span>
                                <span class="meta-value">{{ selectedTemplate.name }}</span>
                            </div>
                            <div class="meta-item">
                                <span class="meta-label">Category</span>
                                <span class="meta-value meta-tag">{{ selectedTemplate.category }}</span>
                            </div>
                            <div class="meta-item">
                                <span class="meta-label">Language</span>
                                <span class="meta-value">{{ selectedTemplate.language }}</span>
                            </div>
                        </div>

                        <!-- Tabs for Carousel -->
                        <div class="tabs-container" v-if="isCarousel">
                            <div class="tabs-header">
                                <div
                                    v-for="(tab, idx) in carouselTabs"
                                    :key="idx"
                                    class="tab-item"
                                    :class="{ active: activeTab === idx }"
                                    @click="activeTab = idx"
                                >
                                    {{ tab }}
                                </div>
                            </div>
                        </div>

                        <!-- Normal Template Configuration -->
                        <template v-if="!isCarousel">
                            <!-- Header Media/URL Selection -->
                            <div class="config-block" v-if="isMediaHeader">
                                <div class="block-header">
                                    <span class="block-title">Header {{ headerComponent.format }}</span>
                                    <span class="block-hint">Required</span>
                                </div>
                                
                                <!-- Media Source Selection -->
                                <div class="media-source-selection">
                                    <label class="radio-option">
                                        <input type="radio" :value="false" v-model="useHeaderUrl" />
                                        <span>Upload Media</span>
                                    </label>
                                    <label class="radio-option">
                                        <input type="radio" :value="true" v-model="useHeaderUrl" />
                                        <span>Use URL</span>
                                    </label>
                                </div>

                                <!-- URL Input -->
                                <div class="media-url-input" v-if="useHeaderUrl">
                                    <div class="param-mode-selection">
                                        <label class="mode-option">
                                            <input type="radio" :value="'static'" v-model="headerImageUrlMode" />
                                            <span>Static</span>
                                        </label>
                                        <label class="mode-option">
                                            <input type="radio" :value="'dynamic'" v-model="headerImageUrlMode" />
                                            <span>Dinamik</span>
                                        </label>
                                    </div>
                                    <input
                                        v-if="headerImageUrlMode === 'static'"
                                        v-model="headerParams[0]"
                                        type="text"
                                        :placeholder="getTemplateHeaderAssetUrl() || getHeaderParamPlaceholder()"
                                        class="param-field"
                                    />
                                    <a-select
                                        v-else
                                        v-model="headerParams[0]"
                                        style="width: 100%"
                                        placeholder="Select a variable"
                                        class="param-field"
                                    >
                                        <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                            {{ attr }}
                                        </a-select-option>
                                    </a-select>
                                    <div class="url-hint" v-if="getTemplateHeaderAssetUrl() && !headerParams[0]">
                                        Template has a default media. Leave empty to use it, or enter a custom URL.
                                    </div>
                                </div>

                                <!-- Upload Area -->
                                <div class="media-area" v-else>
                                    <!-- Uploaded Media -->
                                    <div class="media-uploaded" v-if="media">
                                        <img v-if="headerComponent.format === 'IMAGE'" :src="media.data" alt="Preview" />
                                        <video v-else-if="headerComponent.format === 'VIDEO'" :src="media.data" controls />
                                        <div v-else class="doc-preview">
                                            <a-icon type="file" />
                                            <span>{{ media.name }}</span>
                                        </div>
                                        <button class="remove-media" @click="removeMedia">
                                            <a-icon type="close" />
                                        </button>
                                    </div>
                                    
                                    <!-- Template Default Media -->
                                    <template v-else-if="getTemplateHeaderAssetUrl()">
                                        <div class="media-uploaded template-media">
                                            <img v-if="headerComponent.format === 'IMAGE'" :src="getTemplateHeaderAssetUrl()" alt="Template Media" />
                                            <video v-else-if="headerComponent.format === 'VIDEO'" :src="getTemplateHeaderAssetUrl()" controls />
                                            <div v-else class="doc-preview">
                                                <a-icon type="file" />
                                                <span>Template Media</span>
                                            </div>
                                            <div class="template-media-badge">Template Default</div>
                                        </div>
                                        <label class="upload-area small">
                                            <input
                                                type="file"
                                                :accept="getAcceptType()"
                                                @change="handleFileUpload"
                                            />
                                            <a-icon type="upload" />
                                            <span class="upload-label">Upload different media</span>
                                        </label>
                                    </template>
                                    
                                    <!-- Upload Area -->
                                    <label class="upload-area" v-else>
                                        <input
                                            type="file"
                                            :accept="getAcceptType()"
                                            @change="handleFileUpload"
                                        />
                                        <a-icon type="cloud-upload" />
                                        <span class="upload-label">Click to upload</span>
                                        <span class="upload-format">{{ getMediaHint() }}</span>
                                    </label>
                                </div>
                            </div>

                            <!-- Header Text Parameters -->
                            <div class="config-block" v-if="headerComponent && headerComponent.format === 'TEXT' && headerParams.length > 0">
                                <div class="block-header">
                                    <span class="block-title">Header Variables</span>
                                </div>
                                <div class="params-list">
                                    <div v-for="(param, index) in headerParams" :key="'h-' + index" class="param-row">
                                        <div class="param-info">
                                            <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                            <span class="param-ctx">{{ getParamContext(headerComponent.text, index) }}</span>
                                        </div>
                                        <div class="param-controls">
                                            <div class="param-mode-selection">
                                                <label class="mode-option">
                                                    <input type="radio" :value="'static'" v-model="headerParamModes[index]" />
                                                    <span>Static</span>
                                                </label>
                                                <label class="mode-option">
                                                    <input type="radio" :value="'dynamic'" v-model="headerParamModes[index]" />
                                                    <span>Dinamik</span>
                                                </label>
                                            </div>
                                            <input
                                                v-if="headerParamModes[index] === 'static'"
                                                v-model="headerParams[index]"
                                                type="text"
                                                :placeholder="getHeaderParamPlaceholder()"
                                                class="param-field"
                                            />
                                            <a-select
                                                v-else
                                                v-model="headerParams[index]"
                                                style="width: 100%"
                                                placeholder="Select a variable"
                                                class="param-field"
                                            >
                                                <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                    {{ attr }}
                                                </a-select-option>
                                            </a-select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="config-block" v-if="bodyParams.length > 0">
                                <div class="block-header">
                                    <span class="block-title">Body Variables</span>
                                </div>
                                <div class="params-list">
                                    <div v-for="(param, index) in bodyParams" :key="'b-' + index" class="param-row">
                                        <div class="param-info">
                                            <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                            <span class="param-ctx">{{ getParamContext(bodyComponent.text, index) }}</span>
                                        </div>
                                        <div class="param-controls">
                                            <div class="param-mode-selection">
                                                <label class="mode-option">
                                                    <input type="radio" :value="'static'" v-model="bodyParamModes[index]" />
                                                    <span>Static</span>
                                                </label>
                                                <label class="mode-option">
                                                    <input type="radio" :value="'dynamic'" v-model="bodyParamModes[index]" />
                                                    <span>Dinamik</span>
                                                </label>
                                            </div>
                                            <input
                                                v-if="bodyParamModes[index] === 'static'"
                                                v-model="bodyParams[index]"
                                                type="text"
                                                placeholder="Enter value"
                                                class="param-field"
                                            />
                                            <a-select
                                                v-else
                                                v-model="bodyParams[index]"
                                                style="width: 100%"
                                                placeholder="Select a variable"
                                                class="param-field"
                                            >
                                                <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                    {{ attr }}
                                                </a-select-option>
                                            </a-select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="config-block" v-if="buttonParams.length > 0">
                                <div class="block-header">
                                    <span class="block-title">Button Variables</span>
                                </div>
                                <div class="params-list">
                                    <div v-for="(param, index) in buttonParams" :key="'btn-' + index" class="param-row">
                                        <div class="param-info">
                                            <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                            <span class="param-ctx">{{ getButtonParamContext(index) }}</span>
                                        </div>
                                        <div class="param-controls">
                                            <div class="param-mode-selection">
                                                <label class="mode-option">
                                                    <input type="radio" :value="'static'" v-model="buttonParamModes[index]" />
                                                    <span>Static</span>
                                                </label>
                                                <label class="mode-option">
                                                    <input type="radio" :value="'dynamic'" v-model="buttonParamModes[index]" />
                                                    <span>Dinamik</span>
                                                </label>
                                            </div>
                                            <input
                                                v-if="buttonParamModes[index] === 'static'"
                                                v-model="buttonParams[index]"
                                                type="text"
                                                placeholder="Enter value"
                                                class="param-field"
                                            />
                                            <a-select
                                                v-else
                                                v-model="buttonParams[index]"
                                                style="width: 100%"
                                                placeholder="Select a variable"
                                                class="param-field"
                                            >
                                                <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                    {{ attr }}
                                                </a-select-option>
                                            </a-select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Limited Time Offer -->
                            <div class="config-block" v-if="limitedTimeOfferComponent">
                                <div class="block-header">
                                    <span class="block-title">Limited Time Offer</span>
                                    <span class="block-hint">Required</span>
                                </div>
                                <div class="params-list">
                                    <div class="param-row">
                                        <div class="param-info">
                                            <span class="param-idx">
                                                <a-icon type="calendar" />
                                            </span>
                                            <span class="param-ctx">Offer expiration date</span>
                                        </div>
                                        <div class="param-controls">
                                            <div class="param-mode-selection">
                                                <label class="mode-option">
                                                    <input type="radio" :value="'static'" v-model="expirationDateMode" />
                                                    <span>Static</span>
                                                </label>
                                                <label class="mode-option">
                                                    <input type="radio" :value="'dynamic'" v-model="expirationDateMode" />
                                                    <span>Dinamik</span>
                                                </label>
                                            </div>
                                            <a-date-picker
                                                v-if="expirationDateMode === 'static'"
                                                v-model="expirationDate"
                                                :show-time="true"
                                                format="YYYY-MM-DD HH:mm"
                                                placeholder="Select expiration date"
                                                style="width: 100%"
                                                :disabled-date="disabledDate"
                                            />
                                            <a-select
                                                v-else
                                                v-model="expirationDateAttribute"
                                                style="width: 100%"
                                                placeholder="Select a variable"
                                                class="param-field"
                                            >
                                                <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                    {{ attr }}
                                                </a-select-option>
                                            </a-select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- No Params State -->
                            <div class="ready-state" v-if="!hasAnyParams && !isMediaHeader && !limitedTimeOfferComponent">
                                <div class="ready-icon">
                                    <a-icon type="check" />
                                </div>
                                <div class="ready-text">Ready to send</div>
                                <div class="ready-hint">This template requires no parameters</div>
                            </div>
                        </template>

                        <!-- Carousel Configuration -->
                        <template v-else>
                            <!-- Main Message Tab -->
                            <template v-if="activeTab === 0">
                                <div class="config-block" v-if="bodyParams.length > 0">
                                    <div class="block-header">
                                        <span class="block-title">Body Variables</span>
                                    </div>
                                    <div class="params-list">
                                        <div v-for="(param, index) in bodyParams" :key="'b-' + index" class="param-row">
                                            <div class="param-info">
                                                <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                                <span class="param-ctx">{{ getParamContext(bodyComponent.text, index) }}</span>
                                            </div>
                                            <div class="param-controls">
                                                <div class="param-mode-selection">
                                                    <label class="mode-option">
                                                        <input type="radio" :value="'static'" v-model="bodyParamModes[index]" />
                                                        <span>Static</span>
                                                    </label>
                                                    <label class="mode-option">
                                                        <input type="radio" :value="'dynamic'" v-model="bodyParamModes[index]" />
                                                        <span>Dinamik</span>
                                                    </label>
                                                </div>
                                                <input
                                                    v-if="bodyParamModes[index] === 'static'"
                                                    v-model="bodyParams[index]"
                                                    type="text"
                                                    placeholder="Enter value"
                                                    class="param-field"
                                                />
                                                <a-select
                                                    v-else
                                                    v-model="bodyParams[index]"
                                                    style="width: 100%"
                                                    placeholder="Select a variable"
                                                    class="param-field"
                                                >
                                                    <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                        {{ attr }}
                                                    </a-select-option>
                                                </a-select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="ready-state" v-if="bodyParams.length === 0">
                                    <div class="ready-icon">
                                        <a-icon type="check" />
                                    </div>
                                    <div class="ready-text">No variables</div>
                                    <div class="ready-hint">Main message has no parameters</div>
                                </div>
                            </template>

                            <!-- Carousel Card Tabs -->
                            <template v-else>
                                <div
                                    v-for="(card, cardIndex) in carouselCards"
                                    :key="'card-' + cardIndex"
                                    v-show="activeTab === cardIndex + 1"
                                >
                                    <!-- Card Header Media -->
                                    <div
                                        class="config-block"
                                        v-if="hasCarouselCardHeaderVariable(cardIndex)"
                                    >
                                        <div class="block-header">
                                            <span class="block-title">Header {{ getCarouselCardHeaderFormat(cardIndex) }}</span>
                                            <span class="block-hint">Required</span>
                                        </div>
                                        
                                        <!-- Media Source Selection -->
                                        <div class="media-source-selection">
                                            <label class="radio-option">
                                                <input type="radio" :value="false" v-model="carouselCardUseUrl[cardIndex]" />
                                                <span>Upload Media</span>
                                            </label>
                                            <label class="radio-option">
                                                <input type="radio" :value="true" v-model="carouselCardUseUrl[cardIndex]" />
                                                <span>Use URL</span>
                                            </label>
                                        </div>

                                        <!-- URL Input -->
                                        <div class="media-url-input" v-if="carouselCardUseUrl[cardIndex]">
                                            <div class="param-mode-selection">
                                                <label class="mode-option">
                                                    <input type="radio" :value="'static'" v-model="carouselCardImageUrlMode[cardIndex]" />
                                                    <span>Static</span>
                                                </label>
                                                <label class="mode-option">
                                                    <input type="radio" :value="'dynamic'" v-model="carouselCardImageUrlMode[cardIndex]" />
                                                    <span>Dinamik</span>
                                                </label>
                                            </div>
                                            <input
                                                v-if="carouselCardImageUrlMode[cardIndex] === 'static'"
                                                v-model="carouselCardParams[cardIndex].header[0]"
                                                type="text"
                                                :placeholder="getCarouselCardAssetUrl(cardIndex) || getCarouselHeaderParamPlaceholder(cardIndex)"
                                                class="param-field"
                                            />
                                            <a-select
                                                v-else
                                                v-model="carouselCardParams[cardIndex].header[0]"
                                                style="width: 100%"
                                                placeholder="Select a variable"
                                                class="param-field"
                                            >
                                                <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                    {{ attr }}
                                                </a-select-option>
                                            </a-select>
                                            <div class="url-hint" v-if="getCarouselCardAssetUrl(cardIndex) && !carouselCardParams[cardIndex].header[0]">
                                                Card has a default media. Leave empty to use it, or enter a custom URL.
                                            </div>
                                        </div>

                                        <!-- Upload Area -->
                                        <div class="media-area" v-else>
                                            <!-- Uploaded Media -->
                                            <div class="media-uploaded" v-if="carouselCardMedia[cardIndex]">
                                                <img v-if="getCarouselCardHeaderFormat(cardIndex) === 'IMAGE'" :src="carouselCardMedia[cardIndex].data" alt="Preview" />
                                                <video v-else-if="getCarouselCardHeaderFormat(cardIndex) === 'VIDEO'" :src="carouselCardMedia[cardIndex].data" controls />
                                                <div v-else class="doc-preview">
                                                    <a-icon type="file" />
                                                    <span>{{ carouselCardMedia[cardIndex].name }}</span>
                                                </div>
                                                <button class="remove-media" @click="removeCarouselCardMedia(cardIndex)">
                                                    <a-icon type="close" />
                                                </button>
                                            </div>
                                            
                                            <!-- Template Default Media -->
                                            <template v-else-if="getCarouselCardAssetUrl(cardIndex)">
                                                <div class="media-uploaded template-media">
                                                    <img v-if="getCarouselCardHeaderFormat(cardIndex) === 'IMAGE'" :src="getCarouselCardAssetUrl(cardIndex)" alt="Template Media" />
                                                    <video v-else-if="getCarouselCardHeaderFormat(cardIndex) === 'VIDEO'" :src="getCarouselCardAssetUrl(cardIndex)" controls />
                                                    <div v-else class="doc-preview">
                                                        <a-icon type="file" />
                                                        <span>Template Media</span>
                                                    </div>
                                                    <div class="template-media-badge">Template Default</div>
                                                </div>
                                                <label class="upload-area small">
                                                    <input
                                                        type="file"
                                                        :accept="getCarouselCardAcceptType(cardIndex)"
                                                        @change="handleCarouselCardFileUpload($event, cardIndex)"
                                                    />
                                                    <a-icon type="upload" />
                                                    <span class="upload-label">Upload different media</span>
                                                </label>
                                            </template>
                                            
                                            <!-- Upload Area -->
                                            <label class="upload-area" v-else>
                                                <input
                                                    type="file"
                                                    :accept="getCarouselCardAcceptType(cardIndex)"
                                                    @change="handleCarouselCardFileUpload($event, cardIndex)"
                                                />
                                                <a-icon type="cloud-upload" />
                                                <span class="upload-label">Click to upload</span>
                                                <span class="upload-format">{{ getCarouselCardMediaHint(cardIndex) }}</span>
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Card Body Variables -->
                                    <div
                                        class="config-block"
                                        v-if="carouselCardParams[cardIndex].body.length > 0"
                                    >
                                        <div class="block-header">
                                            <span class="block-title">Body Variables</span>
                                        </div>
                                        <div class="params-list">
                                            <div
                                                v-for="(param, paramIndex) in carouselCardParams[cardIndex].body"
                                                :key="'body-' + paramIndex"
                                                class="param-row"
                                            >
                                                <div class="param-info">
                                                    <span class="param-idx">{{ getPlaceholder(paramIndex) }}</span>
                                                    <span class="param-ctx">{{ getCarouselCardBodyContext(cardIndex, paramIndex) }}</span>
                                                </div>
                                                <div class="param-controls">
                                                    <div class="param-mode-selection">
                                                        <label class="mode-option">
                                                            <input type="radio" :value="'static'" v-model="carouselCardParamModes[cardIndex].body[paramIndex]" />
                                                            <span>Static</span>
                                                        </label>
                                                        <label class="mode-option">
                                                            <input type="radio" :value="'dynamic'" v-model="carouselCardParamModes[cardIndex].body[paramIndex]" />
                                                            <span>Dinamik</span>
                                                        </label>
                                                    </div>
                                                    <input
                                                        v-if="carouselCardParamModes[cardIndex].body[paramIndex] === 'static'"
                                                        v-model="carouselCardParams[cardIndex].body[paramIndex]"
                                                        type="text"
                                                        placeholder="Enter value"
                                                        class="param-field"
                                                    />
                                                    <a-select
                                                        v-else
                                                        v-model="carouselCardParams[cardIndex].body[paramIndex]"
                                                        style="width: 100%"
                                                        placeholder="Select a variable"
                                                        class="param-field"
                                                    >
                                                        <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                            {{ attr }}
                                                        </a-select-option>
                                                    </a-select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Card Button Variables -->
                                    <div
                                        class="config-block"
                                        v-if="carouselCardParams[cardIndex].buttons.length > 0"
                                    >
                                        <div class="block-header">
                                            <span class="block-title">Button Variables</span>
                                        </div>
                                        <div class="params-list">
                                            <div
                                                v-for="(param, paramIndex) in carouselCardParams[cardIndex].buttons"
                                                :key="'btn-' + paramIndex"
                                                class="param-row"
                                            >
                                                <div class="param-info">
                                                    <span class="param-idx">{{ getPlaceholder(paramIndex) }}</span>
                                                    <span class="param-ctx">URL suffix</span>
                                                </div>
                                                <div class="param-controls">
                                                    <div class="param-mode-selection">
                                                        <label class="mode-option">
                                                            <input type="radio" :value="'static'" v-model="carouselCardParamModes[cardIndex].buttons[paramIndex]" />
                                                            <span>Static</span>
                                                        </label>
                                                        <label class="mode-option">
                                                            <input type="radio" :value="'dynamic'" v-model="carouselCardParamModes[cardIndex].buttons[paramIndex]" />
                                                            <span>Dinamik</span>
                                                        </label>
                                                    </div>
                                                    <input
                                                        v-if="carouselCardParamModes[cardIndex].buttons[paramIndex] === 'static'"
                                                        v-model="carouselCardParams[cardIndex].buttons[paramIndex]"
                                                        type="text"
                                                        placeholder="Enter value"
                                                        class="param-field"
                                                    />
                                                    <a-select
                                                        v-else
                                                        v-model="carouselCardParams[cardIndex].buttons[paramIndex]"
                                                        style="width: 100%"
                                                        placeholder="Select a variable"
                                                        class="param-field"
                                                    >
                                                        <a-select-option v-for="attr in getAvailableParameters()" :key="attr" :value="attr">
                                                            {{ attr }}
                                                        </a-select-option>
                                                    </a-select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="ready-state"
                                        v-if="carouselCardParams[cardIndex].header.length === 0 &&
                                              carouselCardParams[cardIndex].body.length === 0 &&
                                              carouselCardParams[cardIndex].buttons.length === 0"
                                    >
                                        <div class="ready-icon">
                                            <a-icon type="check" />
                                        </div>
                                        <div class="ready-text">No variables</div>
                                        <div class="ready-hint">This card has no parameters</div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </div>

                    <!-- Right: Preview -->
                    <div class="preview-area">
                        <WhatsappMessagePreview
                            :templateType="previewTemplateType"
                            :headerMediaType="previewHeaderMediaType"
                            :headerMediaURL="previewHeaderMediaURL"
                            :headerText="previewHeaderText"
                            :body="previewBody"
                            :footer="previewFooter"
                            :buttons="previewButtons"
                            :cards="previewCards"
                            :limitedTimeOffer="previewLimitedTimeOffer"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import WhatsappMessagePreview from "../../../../WhatsappBusinessGupshup/WhatsappMessagePreview.vue";
import { mapState } from "vuex";
import { uploadAsset } from "../../../../../../api/assetUpload";
import axios from "axios";
import WHATSAPP_API from "../../../../../../api/whatsapp";
import _ from "lodash";

export default {
    name: "MetaTemplateMessageSelector",
    components: { WhatsappMessagePreview },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            templates: [],
            loading: false,
            selectedTemplateId: null,
            selectedTemplate: null,
            media: null,
            headerParams: [],
            bodyParams: [],
            buttonParams: [],
            activeTab: 0,
            carouselCardParams: [],
            useHeaderUrl: false,
            carouselCardMedia: [],
            carouselCardUseUrl: [],
            expirationDate: null, // For limited time offer
            selectedCustomerGroup: null,
            // Mode arrays: 'dynamic' or 'static' for each variable
            headerParamModes: [],
            bodyParamModes: [],
            buttonParamModes: [],
            carouselCardParamModes: [], // Array of { header: [], body: [], buttons: [] }
            headerImageUrlMode: "static", // Mode for header image URL
            carouselCardImageUrlMode: [], // Array of modes for carousel card image URLs
            expirationDateMode: "static", // Mode for expiration date (static/dynamic)
            expirationDateAttribute: null, // Attribute name for dynamic expiration date
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
        }),
        components() {
            return this.selectedTemplate?.components || [];
        },
        headerComponent() {
            return this.components.find((c) => c.type === "HEADER");
        },
        bodyComponent() {
            return this.components.find((c) => c.type === "BODY");
        },
        footerComponent() {
            return this.components.find((c) => c.type === "FOOTER");
        },
        buttonsComponent() {
            return this.components.find((c) => c.type === "BUTTONS");
        },
        limitedTimeOfferComponent() {
            return this.components.find((c) => c.type === "LIMITED_TIME_OFFER");
        },
        carouselComponent() {
            return this.components.find((c) => c.type === "CAROUSEL");
        },
        isCarousel() {
            return !!this.carouselComponent;
        },
        carouselCards() {
            if (!this.carouselComponent || !this.carouselComponent.cards) return [];
            return this.carouselComponent.cards;
        },
        carouselTabs() {
            const tabs = ["Main Message"];
            this.carouselCards.forEach((_, index) => {
                tabs.push(`Card ${index + 1}`);
            });
            return tabs;
        },
        isMediaHeader() {
            if (!this.headerComponent) return false;
            return ["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format);
        },
        hasAnyParams() {
            if (this.isCarousel) {
                return this.bodyParams.length > 0 || 
                       this.limitedTimeOfferComponent ||
                       this.carouselCardParams.some(card =>
                           card.header.length > 0 || card.body.length > 0 || card.buttons.length > 0
                       );
            }
            return this.headerParams.length > 0 || 
                   this.bodyParams.length > 0 || 
                   this.buttonParams.length > 0 || 
                   !!this.limitedTimeOfferComponent;
        },
        // Preview computed properties
        previewTemplateType() {
            if (this.isCarousel) return "CAROUSEL";
            if (this.limitedTimeOfferComponent) return "LIMITED_TIME_OFFER";
            return this.selectedTemplate?.category || "CUSTOM_MESSAGE";
        },
        previewHeaderMediaType() {
            if (!this.headerComponent) return null;
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                return this.headerComponent.format;
            }
            return null;
        },
        previewHeaderMediaURL() {
            if (this.headerParams.length > 0 && this.headerParams[0]) {
                return this.headerParams[0];
            }
            if (this.media) return this.media.data;
            
            const headerAssetCdn = this.selectedTemplate?.mediaPayload?.headerAsset?.cdnUrl;
            if (headerAssetCdn) return headerAssetCdn;
            
            if (this.selectedTemplate?.cdnUrl) return this.selectedTemplate.cdnUrl;
            
            return null;
        },
        previewHeaderText() {
            if (!this.headerComponent || this.headerComponent.format !== "TEXT") return "";
            let text = this.headerComponent.text || "";
            this.headerParams.forEach((value, index) => {
                const placeholder = "{{" + (index + 1) + "}}";
                let displayValue = "";
                if (value) {
                    const mode = this.headerParamModes[index];
                    if (mode === "dynamic") {
                        displayValue = "[" + value + "]";
                    } else {
                        displayValue = value;
                    }
                } else {
                    // Use example value if available
                    const exampleValue = this.getExampleValue(this.headerComponent, index);
                    if (exampleValue) {
                        displayValue = "[" + exampleValue + "]";
                    } else {
                        displayValue = "[" + (index + 1) + "]";
                    }
                }
                text = text.replace(placeholder, displayValue);
            });
            return text;
        },
        previewBody() {
            if (!this.bodyComponent) return "";
            let text = this.bodyComponent.text || "";
            this.bodyParams.forEach((value, index) => {
                const placeholder = "{{" + (index + 1) + "}}";
                let displayValue = "";
                if (value) {
                    const mode = this.bodyParamModes[index];
                    if (mode === "dynamic") {
                        displayValue = "[" + value + "]";
                    } else {
                        displayValue = value;
                    }
                } else {
                    // Use example value if available
                    const exampleValue = this.getExampleValue(this.bodyComponent, index);
                    if (exampleValue) {
                        displayValue = "[" + exampleValue + "]";
                    } else {
                        displayValue = "[" + (index + 1) + "]";
                    }
                }
                text = text.replace(placeholder, displayValue);
            });
            return text;
        },
        previewFooter() {
            return this.footerComponent ? this.footerComponent.text : null;
        },
        previewButtons() {
            if (!this.buttonsComponent) return [];
            const buttons = this.buttonsComponent.buttons || [];
            
            // COPY_CODE button'larına parametre değerlerini ekle
            let buttonParamIndex = 0;
            return buttons.map(btn => {
                const button = { ...btn };
                
                if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
                    // URL button parametreleri
                    const matches = btn.url.match(/\{\{\d+\}\}/g);
                    if (matches && buttonParamIndex < this.buttonParams.length) {
                        let url = btn.url;
                        matches.forEach(() => {
                            const paramValue = this.buttonParams[buttonParamIndex];
                            const mode = this.buttonParamModes[buttonParamIndex];
                            let displayValue = "";
                            
                            if (paramValue) {
                                if (mode === "dynamic") {
                                    displayValue = "[" + paramValue + "]";
                                } else {
                                    displayValue = paramValue;
                                }
                            } else {
                                displayValue = "[suffix]";
                            }
                            
                            url = url.replace(/\{\{\d+\}\}/, displayValue);
                            buttonParamIndex++;
                        });
                        button.url = url;
                    }
                } else if (btn.type === "COPY_CODE") {
                    // COPY_CODE button parametresi - preview'da sadece text gösterilir
                    button.text = "Copy code"; // COPY_CODE button text'i sabit
                    // Parametre değeri backend'e gönderilecek ama preview'da gösterilmez
                    if (buttonParamIndex < this.buttonParams.length) {
                        buttonParamIndex++;
                    }
                }
                
                return button;
            });
        },
        previewCards() {
            if (!this.isCarousel) return [];
            return this.carouselCards.map((card, cardIndex) => {
                const cardComponents = card.components || [];
                const headerComp = cardComponents.find(c => c.type === "HEADER");
                const bodyComp = cardComponents.find(c => c.type === "BODY");
                const buttonsComp = cardComponents.find(c => c.type === "BUTTONS");

                let mediaUrl = null;
                let headerType = null;

                if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                    headerType = headerComp.format;
                    const cardParam = this.carouselCardParams[cardIndex];
                    
                    if (cardParam && cardParam.header && cardParam.header.length > 0 && cardParam.header[0]) {
                        mediaUrl = cardParam.header[0];
                    } else if (this.carouselCardMedia[cardIndex]) {
                        mediaUrl = this.carouselCardMedia[cardIndex].data;
                    } else {
                        const cardAssetCdn = this.selectedTemplate?.mediaPayload?.cardAssets?.[cardIndex]?.cdnUrl;
                        if (cardAssetCdn) {
                            mediaUrl = cardAssetCdn;
                        }
                    }
                }

                let sampleText = "";
                if (bodyComp && bodyComp.text) {
                    sampleText = bodyComp.text;
                    const cardParam = this.carouselCardParams[cardIndex];
                    const cardMode = this.carouselCardParamModes[cardIndex];
                    if (cardParam && cardParam.body) {
                        cardParam.body.forEach((value, index) => {
                            const placeholder = "{{" + (index + 1) + "}}";
                            let displayValue = "";
                            if (value) {
                                const mode = cardMode && cardMode.body ? cardMode.body[index] : "static";
                                if (mode === "dynamic") {
                                    displayValue = "[" + value + "]";
                                } else {
                                    displayValue = value;
                                }
                            } else {
                                // Use example value if available
                                const exampleValue = this.getExampleValue(bodyComp, index);
                                if (exampleValue) {
                                    displayValue = "[" + exampleValue + "]";
                                } else {
                                    displayValue = "[" + (index + 1) + "]";
                                }
                            }
                            sampleText = sampleText.replace(placeholder, displayValue);
                        });
                    }
                }

                // Button parametrelerini işle
                let previewButtons = [];
                if (buttonsComp && buttonsComp.buttons) {
                    const cardParam = this.carouselCardParams[cardIndex];
                    const cardMode = this.carouselCardParamModes[cardIndex];
                    let buttonParamIndex = 0;
                    
                    previewButtons = buttonsComp.buttons.map(btn => {
                        const button = { ...btn };
                        
                        if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
                            const matches = btn.url.match(/\{\{\d+\}\}/g);
                            if (matches && cardParam.buttons) {
                                let url = btn.url;
                                matches.forEach(() => {
                                    if (buttonParamIndex < cardParam.buttons.length) {
                                        const paramValue = cardParam.buttons[buttonParamIndex];
                                        const mode = cardMode && cardMode.buttons ? cardMode.buttons[buttonParamIndex] : "static";
                                        let displayValue = "";
                                        
                                        if (paramValue) {
                                            if (mode === "dynamic") {
                                                displayValue = "[" + paramValue + "]";
                                            } else {
                                                displayValue = paramValue;
                                            }
                                        } else {
                                            displayValue = "[suffix]";
                                        }
                                        
                                        url = url.replace(/\{\{\d+\}\}/, displayValue);
                                        buttonParamIndex++;
                                    }
                                });
                                button.url = url;
                            }
                        } else if (btn.type === "COPY_CODE") {
                            button.text = "Copy code"; // COPY_CODE button text'i sabit
                            // Parametre değeri backend'e gönderilecek ama preview'da gösterilmez
                            if (cardParam.buttons && buttonParamIndex < cardParam.buttons.length) {
                                buttonParamIndex++;
                            }
                        }
                        
                        return button;
                    });
                }

                return {
                    mediaUrl,
                    headerType,
                    sampleText,
                    buttons: previewButtons
                };
            });
        },
        previewLimitedTimeOffer() {
            if (!this.limitedTimeOfferComponent) return null;
            
            const lto = {
                text: this.limitedTimeOfferComponent.limited_time_offer?.text || "",
                hasExpiration: this.limitedTimeOfferComponent.limited_time_offer?.has_expiration !== false,
            };
            
            // Expiration date ekle
            if (this.expirationDateMode === "static" && this.expirationDate) {
                lto.expirationDate = this.expirationDate;
            } else if (this.expirationDateMode === "dynamic" && this.expirationDateAttribute) {
                // Dinamik mod için placeholder göster
                lto.expirationDate = "[" + this.expirationDateAttribute + "]";
            }
            
            return lto;
        },
    },
    async mounted() {
        await this.loadTemplates();
        this.getCustomerGroupDetail();
    },
    methods: {
        async loadTemplates() {
            this.loading = true;
            try {
                const response = await this.$store.dispatch("whatsapp/getTemplates", this.channel._id);
                this.templates = response.filter(t => t.format === "meta" && t.status === "APPROVED");
                if (this.templates.length > 0) {
                    this.selectedTemplateId = this.templates[0]._id;
                }
            } catch (error) {
                console.error("Failed to load templates", error);
                this.$message.error("Failed to load templates");
            } finally {
                this.loading = false;
            }
        },
        getPlaceholder(index) {
            return "{{" + (index + 1) + "}}";
        },
        getButtonParamContext(index) {
            if (!this.buttonsComponent || !this.buttonsComponent.buttons) return "URL suffix";
            
            let paramIndex = 0;
            for (const btn of this.buttonsComponent.buttons) {
                if (btn.type === "URL" && btn.url) {
                    const matches = btn.url.match(/\{\{\d+\}\}/g);
                    if (matches) {
                        for (let i = 0; i < matches.length; i++) {
                            if (paramIndex === index) {
                                return `URL suffix (${btn.text || 'Button'})`;
                            }
                            paramIndex++;
                        }
                    }
                } else if (btn.type === "COPY_CODE") {
                    if (paramIndex === index) {
                        return `Copy code (${btn.text || 'Button'})`;
                    }
                    paramIndex++;
                }
            }
            return "URL suffix";
        },
        getExampleValue(component, index) {
            if (!component || !component.example) return null;
            
            // Check for body_text (positional parameters)
            if (component.example.body_text && Array.isArray(component.example.body_text)) {
                const exampleArray = component.example.body_text[0];
                if (Array.isArray(exampleArray) && exampleArray[index] !== undefined) {
                    return exampleArray[index];
                }
            }
            
            // Check for header_text (header positional parameters)
            if (component.example.header_text && Array.isArray(component.example.header_text)) {
                const exampleArray = component.example.header_text[0];
                if (Array.isArray(exampleArray) && exampleArray[index] !== undefined) {
                    return exampleArray[index];
                }
            }
            
            // Check for named parameters (body_text_named_params)
            if (component.example.body_text_named_params && Array.isArray(component.example.body_text_named_params)) {
                const param = component.example.body_text_named_params[index];
                if (param && param.example) {
                    return param.example;
                }
            }
            
            return null;
        },
        initializeParams() {
            if (this.isCarousel) {
                if (this.bodyComponent && this.bodyComponent.text) {
                    const matches = this.bodyComponent.text.match(/\{\{\d+\}\}/g);
                    this.bodyParams = matches ? matches.map(() => "") : [];
                    this.bodyParamModes = matches ? matches.map(() => "static") : [];
                }

                this.carouselCardMedia = [];
                this.carouselCardUseUrl = [];
                this.carouselCardImageUrlMode = [];
                for (let i = 0; i < this.carouselCards.length; i++) {
                    this.$set(this.carouselCardMedia, i, null);
                    this.$set(this.carouselCardUseUrl, i, false);
                    this.$set(this.carouselCardImageUrlMode, i, "static");
                }
                
                this.carouselCardParams = this.carouselCards.map(card => {
                    const cardComponents = card.components || [];
                    const headerComp = cardComponents.find(c => c.type === "HEADER");
                    const bodyComp = cardComponents.find(c => c.type === "BODY");
                    const buttonsComp = cardComponents.find(c => c.type === "BUTTONS");

                    const cardParam = {
                        header: [],
                        body: [],
                        buttons: [],
                    };

                    if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                        cardParam.header = [""];
                    }

                    if (bodyComp && bodyComp.text) {
                        const matches = bodyComp.text.match(/\{\{\d+\}\}/g);
                        cardParam.body = matches ? matches.map(() => "") : [];
                    }

                    if (buttonsComp && buttonsComp.buttons) {
                        let buttonParamCount = 0;
                        buttonsComp.buttons.forEach(btn => {
                            if (btn.type === "URL" && btn.url) {
                                const matches = btn.url.match(/\{\{\d+\}\}/g);
                                if (matches) buttonParamCount += matches.length;
                            } else if (btn.type === "COPY_CODE") {
                                // COPY_CODE button'ları için her button başına 1 parametre
                                buttonParamCount += 1;
                            }
                        });
                        cardParam.buttons = new Array(buttonParamCount).fill("");
                    }

                    return cardParam;
                });

                // Initialize carousel card param modes
                this.carouselCardParamModes = this.carouselCards.map(card => {
                    const cardComponents = card.components || [];
                    const bodyComp = cardComponents.find(c => c.type === "BODY");
                    const buttonsComp = cardComponents.find(c => c.type === "BUTTONS");

                    const cardMode = {
                        header: [],
                        body: [],
                        buttons: [],
                    };

                    if (bodyComp && bodyComp.text) {
                        const matches = bodyComp.text.match(/\{\{\d+\}\}/g);
                        cardMode.body = matches ? matches.map(() => "static") : [];
                    }

                    if (buttonsComp && buttonsComp.buttons) {
                        let buttonParamCount = 0;
                        buttonsComp.buttons.forEach(btn => {
                            if (btn.type === "URL" && btn.url) {
                                const matches = btn.url.match(/\{\{\d+\}\}/g);
                                if (matches) buttonParamCount += matches.length;
                            } else if (btn.type === "COPY_CODE") {
                                // COPY_CODE button'ları için her button başına 1 parametre
                                buttonParamCount += 1;
                            }
                        });
                        cardMode.buttons = new Array(buttonParamCount).fill("static");
                    }

                    return cardMode;
                });
            } else {
                if (this.headerComponent) {
                    if (this.headerComponent.text) {
                        const matches = this.headerComponent.text.match(/\{\{\d+\}\}/g);
                        this.headerParams = matches ? matches.map(() => "") : [];
                        this.headerParamModes = matches ? matches.map(() => "static") : [];
                    } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                        this.headerParams = [""];
                        this.headerParamModes = [];
                        this.headerImageUrlMode = "static";
                    }
                }

                if (this.bodyComponent && this.bodyComponent.text) {
                    const matches = this.bodyComponent.text.match(/\{\{\d+\}\}/g);
                    this.bodyParams = matches ? matches.map(() => "") : [];
                    this.bodyParamModes = matches ? matches.map(() => "static") : [];
                }

                if (this.buttonsComponent && this.buttonsComponent.buttons) {
                    let buttonParamCount = 0;
                    this.buttonsComponent.buttons.forEach((btn) => {
                        if (btn.type === "URL" && btn.url) {
                            const matches = btn.url.match(/\{\{\d+\}\}/g);
                            if (matches) buttonParamCount += matches.length;
                        } else if (btn.type === "COPY_CODE") {
                            // COPY_CODE button'ları için her button başına 1 parametre
                            buttonParamCount += 1;
                        }
                    });
                    this.buttonParams = new Array(buttonParamCount).fill("");
                    this.buttonParamModes = new Array(buttonParamCount).fill("static");
                }
            }

            this.emitParams();
        },
        getCustomerGroupDetail() {
            if (this.value.groupId) {
                WHATSAPP_API.BROADCAST.getGroupById(this.value.groupId).then((res) => {
                    this.selectedCustomerGroup = res.data;
                }).catch((error) => {
                    console.error("Failed to load customer group:", error);
                });
            }
        },
        getAvailableParameters() {
            if (this.selectedCustomerGroup) {
                const headContactAttributes = _.get(this.selectedCustomerGroup, "contacts.0.attributes", {}) || {};
                return Object.keys(headContactAttributes);
            }
            return [];
        },
        getParamContext(text, index) {
            if (!text) return "";
            const placeholder = "{{" + (index + 1) + "}}";
            const position = text.indexOf(placeholder);
            if (position === -1) return "";
            
            const start = Math.max(0, position - 20);
            const end = Math.min(text.length, position + placeholder.length + 20);
            let context = text.substring(start, end);
            
            if (start > 0) context = "..." + context;
            if (end < text.length) context = context + "...";
            
            return context;
        },
        getHeaderParamPlaceholder() {
            if (!this.headerComponent) return "Enter value";
            
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                const type = this.headerComponent.format.toLowerCase();
                return `Enter ${type} URL`;
            }
            
            return "Enter value";
        },
        getTemplateHeaderAssetUrl() {
            const headerAssetCdn = this.selectedTemplate?.mediaPayload?.headerAsset?.cdnUrl;
            if (headerAssetCdn) return headerAssetCdn;
            
            if (this.selectedTemplate?.cdnUrl) return this.selectedTemplate.cdnUrl;
            
            return null;
        },
        getCarouselCardAssetUrl(cardIndex) {
            const cardAssetCdn = this.selectedTemplate?.mediaPayload?.cardAssets?.[cardIndex]?.cdnUrl;
            return cardAssetCdn || null;
        },
        hasCarouselCardHeaderVariable(cardIndex) {
            if (!this.carouselCards[cardIndex]) return false;
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            return headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format);
        },
        getCarouselCardBodyContext(cardIndex, paramIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const bodyComp = (card.components || []).find(c => c.type === "BODY");
            if (!bodyComp || !bodyComp.text) return "";
            return this.getParamContext(bodyComp.text, paramIndex);
        },
        getCarouselHeaderParamContext(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            if (!headerComp) return "";
            
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                return `${headerComp.format} URL`;
            }
            
            return "";
        },
        getCarouselHeaderParamPlaceholder(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "Enter value";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            if (!headerComp) return "Enter value";
            
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                const type = headerComp.format.toLowerCase();
                return `Enter ${type} URL`;
            }
            
            return "Enter value";
        },
        getCarouselCardHeaderFormat(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            return headerComp ? headerComp.format : "";
        },
        getAcceptType() {
            if (!this.headerComponent) return "";
            const format = this.headerComponent.format;
            if (format === "IMAGE") return "image/jpeg, image/png";
            if (format === "VIDEO") return "video/mp4";
            if (format === "DOCUMENT") return "application/pdf";
            return "";
        },
        getMediaHint() {
            if (!this.headerComponent) return "";
            const format = this.headerComponent.format;
            if (format === "IMAGE") return "JPEG, PNG • Max 5MB";
            if (format === "VIDEO") return "MP4 • Max 16MB";
            if (format === "DOCUMENT") return "PDF • Max 100MB";
            return "";
        },
        getCarouselCardAcceptType(cardIndex) {
            const format = this.getCarouselCardHeaderFormat(cardIndex);
            if (format === "IMAGE") return "image/jpeg, image/png";
            if (format === "VIDEO") return "video/mp4";
            if (format === "DOCUMENT") return "application/pdf";
            return "";
        },
        getCarouselCardMediaHint(cardIndex) {
            const format = this.getCarouselCardHeaderFormat(cardIndex);
            if (format === "IMAGE") return "JPEG, PNG • Max 5MB";
            if (format === "VIDEO") return "MP4 • Max 16MB";
            if (format === "DOCUMENT") return "PDF • Max 100MB";
            return "";
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.headerComponent.format;

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16MB");
                return;
            }
            if (format === "DOCUMENT" && mb > 100) {
                this.$message.error("Document must be less than 100MB");
                return;
            }

            this.$message.loading("Uploading...", 0);

            try {
                const path = this.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                const cdnUrl = _asset.v2Format && _asset.cdnUrl
                    ? _asset.cdnUrl
                    : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext;

                this.media = {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                };

                this.headerParams[0] = cdnUrl;

                this.$message.destroy();
                this.$message.success("File uploaded successfully");
                
                this.emitParams();
            } catch (error) {
                this.$message.destroy();
                this.$message.error("Upload failed");
                console.error(error);
            }
        },
        removeMedia() {
            this.media = null;
            this.headerParams[0] = "";
            this.emitParams();
        },
        async handleCarouselCardFileUpload(event, cardIndex) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.getCarouselCardHeaderFormat(cardIndex);

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16MB");
                return;
            }
            if (format === "DOCUMENT" && mb > 100) {
                this.$message.error("Document must be less than 100MB");
                return;
            }

            this.$message.loading("Uploading...", 0);

            try {
                const path = this.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                const cdnUrl = _asset.v2Format && _asset.cdnUrl
                    ? _asset.cdnUrl
                    : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext;

                this.$set(this.carouselCardMedia, cardIndex, {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                });

                this.carouselCardParams[cardIndex].header[0] = cdnUrl;

                this.$message.destroy();
                this.$message.success("File uploaded successfully");
                
                this.emitParams();
            } catch (error) {
                this.$message.destroy();
                this.$message.error("Upload failed");
                console.error(error);
            }
        },
        removeCarouselCardMedia(cardIndex) {
            this.$set(this.carouselCardMedia, cardIndex, null);
            this.carouselCardParams[cardIndex].header[0] = "";
            this.emitParams();
        },
        emitParams() {
            const templateParams = this.isCarousel ? {
                body: this.bodyParams,
                carousel: this.carouselCardParams.map(card => ({
                    header: card.header,
                    body: card.body,
                    buttons: card.buttons,
                })),
            } : {
                header: this.headerParams.filter(p => p),
                body: this.bodyParams,
                button: this.buttonParams,
            };

            // Add expiration time for LTO
            if (this.limitedTimeOfferComponent) {
                if (this.expirationDateMode === "static" && this.expirationDate) {
                    templateParams.expirationTimeMs = this.expirationDate.valueOf();
                } else if (this.expirationDateMode === "dynamic" && this.expirationDateAttribute) {
                    // Dinamik mod: attribute adını gönder, BroadcastWorker'da timestamp'e çevrilecek
                    templateParams.expirationDateAttribute = this.expirationDateAttribute;
                }
            }

            const params = {
                selectedTemplate: this.selectedTemplate,
                templateParams: templateParams,
            };

            this.$emit("input", {
                ...this.value,
                ...params,
            });
        },
        disabledDate(current) {
            // Disable past dates
            return current && current < this.$moment().startOf('day');
        },
    },
    watch: {
        selectedTemplateId: {
            handler(val) {
                if (val) {
                    this.selectedTemplate = this.templates.find(t => t._id === val);
                    this.activeTab = 0;
                    this.media = null;
                    this.carouselCardMedia = [];
                    this.expirationDate = null;
                    this.expirationDateMode = "static";
                    this.expirationDateAttribute = null;
                    this.headerParamModes = [];
                    this.bodyParamModes = [];
                    this.buttonParamModes = [];
                    this.carouselCardParamModes = [];
                    this.headerImageUrlMode = "static";
                    this.carouselCardImageUrlMode = [];
                    this.initializeParams();
                }
            },
            immediate: true,
        },
        "value.groupId": {
            handler() {
                this.getCustomerGroupDetail();
            },
            immediate: true,
        },
        headerParams: {
            handler() {
                this.emitParams();
            },
            deep: true,
        },
        bodyParams: {
            handler() {
                this.emitParams();
            },
            deep: true,
        },
        buttonParams: {
            handler() {
                this.emitParams();
            },
            deep: true,
        },
        carouselCardParams: {
            handler() {
                this.emitParams();
            },
            deep: true,
        },
        expirationDate: {
            handler() {
                this.emitParams();
            },
        },
        expirationDateMode: {
            handler() {
                this.emitParams();
            },
        },
        expirationDateAttribute: {
            handler() {
                this.emitParams();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.meta-template-selector {
    .template-selection-section {
        margin-bottom: 24px;

        .section-title {
            font-size: 15px;
            font-weight: 600;
            color: #262626;
            margin-bottom: 12px;
        }
    }

    .template-config-section {
        .config-layout {
            display: flex;
            gap: 24px;
            align-items: flex-start;

            .config-area {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 1px;
                background: #f5f5f5;
                border-radius: 8px;
                overflow: hidden;
            }

            .preview-area {
                width: 400px;
                flex-shrink: 0;
            }
        }
    }

    .meta-block {
        padding: 20px;
        border-bottom: 1px solid #e8e8e8;
        display: flex;
        gap: 24px;
        background: #fff;

        .meta-item {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .meta-label {
                font-size: 11px;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.3px;
                color: #8c8c8c;
            }

            .meta-value {
                font-size: 13px;
                font-weight: 500;
                color: #262626;

                &.meta-tag {
                    display: inline-block;
                    background: #e6f4ff;
                    color: #0958d9;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 600;
                }
            }
        }
    }

    .tabs-container {
        border-bottom: 1px solid #e8e8e8;
        background: #fff;

        .tabs-header {
            display: flex;
            gap: 0;
            overflow-x: auto;

            .tab-item {
                padding: 12px 16px;
                font-size: 13px;
                font-weight: 500;
                color: #595959;
                cursor: pointer;
                border-bottom: 2px solid transparent;
                transition: all 0.2s;
                white-space: nowrap;
                flex-shrink: 0;

                &:hover {
                    color: #1677ff;
                }

                &.active {
                    color: #1677ff;
                    border-bottom-color: #1677ff;
                }
            }
        }
    }

    .config-block {
        padding: 20px;
        border-bottom: 1px solid #e8e8e8;
        background: #fff;
        margin-bottom: 1px;

        .block-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;

            .block-title {
                font-size: 14px;
                font-weight: 600;
                color: #262626;
            }

            .block-hint {
                font-size: 11px;
                color: #8c8c8c;
                background: #f0f0f0;
                padding: 2px 8px;
                border-radius: 4px;
            }
        }

        .media-source-selection {
            display: flex;
            gap: 8px;
            margin-bottom: 16px;
            padding: 4px;
            background: #fafafa;
            border-radius: 8px;

            .radio-option {
                display: flex;
                align-items: center;
                gap: 8px;
                cursor: pointer;
                font-size: 13px;
                color: #595959;
                padding: 8px 12px;
                border-radius: 6px;
                transition: all 0.2s;

                input[type="radio"] {
                    cursor: pointer;
                    width: 16px;
                    height: 16px;
                }

                span {
                    font-weight: 500;
                }

                &:hover {
                    color: #1677ff;
                    background: #f0f5ff;
                }
            }
        }

        .media-url-input {
            margin-bottom: 12px;
            display: flex;
            flex-direction: column;
            gap: 8px;

            .param-mode-selection {
                display: flex;
                gap: 8px;
                padding: 4px;
                background: #fafafa;
                border-radius: 6px;

                .mode-option {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    font-size: 12px;
                    color: #595959;
                    padding: 4px 10px;
                    border-radius: 4px;
                    transition: all 0.2s;

                    input[type="radio"] {
                        cursor: pointer;
                        width: 14px;
                        height: 14px;
                    }

                    span {
                        font-weight: 500;
                    }

                    &:hover {
                        color: #1677ff;
                        background: #f0f5ff;
                    }
                }
            }

            input {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                font-size: 13px;
                outline: none;
                transition: border-color 0.15s;

                &:focus {
                    border-color: #1677ff;
                    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
                }

                &::placeholder {
                    color: #bfbfbf;
                }
            }

            .url-hint {
                margin-top: 6px;
                font-size: 11px;
                color: #8c8c8c;
                line-height: 1.4;
            }
        }

        .media-area {
            .media-uploaded {
                position: relative;
                background: #fafafa;
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                overflow: hidden;

                img, video {
                    width: 100%;
                    max-height: 200px;
                    object-fit: cover;
                    display: block;
                }

                .doc-preview {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 24px 16px;
                    background: linear-gradient(135deg, #f0f5ff 0%, #fafafa 100%);

                    .anticon {
                        font-size: 32px;
                        color: #1677ff;
                    }

                    span {
                        font-size: 14px;
                        font-weight: 500;
                        color: #262626;
                    }
                }

                .remove-media {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    width: 28px;
                    height: 28px;
                    background: rgba(0, 0, 0, 0.6);
                    border: none;
                    border-radius: 50%;
                    color: #fff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;

                    &:hover {
                        background: rgba(255, 77, 79, 0.9);
                        transform: scale(1.1);
                    }

                    .anticon {
                        font-size: 14px;
                    }
                }

                &.template-media {
                    border: 2px solid #91d5ff;
                    background: #f0f5ff;

                    .template-media-badge {
                        position: absolute;
                        top: 12px;
                        left: 12px;
                        background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
                        color: #fff;
                        padding: 6px 12px;
                        font-size: 11px;
                        font-weight: 600;
                        border-radius: 6px;
                        box-shadow: 0 2px 8px rgba(22, 119, 255, 0.3);
                        letter-spacing: 0.3px;
                    }
                }
            }

            .upload-area {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 32px 24px;
                border: 2px dashed #d9d9d9;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;
                background: #fafafa;

                &:hover {
                    border-color: #1677ff;
                    background: #f0f5ff;
                    
                    .anticon {
                        color: #1677ff;
                        transform: scale(1.1);
                    }
                }

                input {
                    display: none;
                }

                .anticon {
                    font-size: 36px;
                    color: #8c8c8c;
                    transition: all 0.2s;
                }

                .upload-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #262626;
                }

                .upload-format {
                    font-size: 12px;
                    color: #8c8c8c;
                }

                &.small {
                    margin-top: 12px;
                    padding: 16px 20px;
                    flex-direction: row;
                    justify-content: center;
                    border: 1px dashed #d9d9d9;
                    background: #fff;

                    .anticon {
                        font-size: 18px;
                        color: #595959;
                    }

                    .upload-label {
                        font-size: 13px;
                        color: #595959;
                    }

                    &:hover {
                        border-color: #1677ff;
                        background: #f0f5ff;

                        .anticon,
                        .upload-label {
                            color: #1677ff;
                        }
                    }
                }
            }
        }

        .params-list {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .param-row {
                .param-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 8px;

                    .param-idx {
                        font-size: 12px;
                        font-weight: 700;
                        color: #1677ff;
                        font-family: 'Monaco', 'Menlo', monospace;
                        background: #e6f4ff;
                        padding: 2px 6px;
                        border-radius: 4px;
                    }

                    .param-ctx {
                        font-size: 12px;
                        color: #8c8c8c;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .param-controls {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    width: 100%;

                    .param-mode-selection {
                        display: flex;
                        gap: 8px;
                        padding: 4px;
                        background: #fafafa;
                        border-radius: 6px;

                        .mode-option {
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            cursor: pointer;
                            font-size: 12px;
                            color: #595959;
                            padding: 4px 10px;
                            border-radius: 4px;
                            transition: all 0.2s;

                            input[type="radio"] {
                                cursor: pointer;
                                width: 14px;
                                height: 14px;
                            }

                            span {
                                font-weight: 500;
                            }

                            &:hover {
                                color: #1677ff;
                                background: #f0f5ff;
                            }
                        }
                    }
                }

                .param-field {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #d9d9d9;
                    border-radius: 6px;
                    font-size: 13px;
                    outline: none;
                    transition: all 0.2s;
                    background: #fff;

                    &:hover {
                        border-color: #40a9ff;
                    }

                    &:focus {
                        border-color: #1677ff;
                        box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
                    }

                    &::placeholder {
                        color: #bfbfbf;
                    }
                }
            }

            .url-hint {
                margin-top: 6px;
                font-size: 11px;
                color: #8c8c8c;
                line-height: 1.4;
            }
        }

        .ant-calendar-picker {
            width: 100%;
        }
    }

    .ready-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 40px;

        .ready-icon {
            width: 56px;
            height: 56px;
            background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
            border: 2px solid #b7eb8f;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
            box-shadow: 0 4px 12px rgba(82, 196, 26, 0.15);

            .anticon {
                font-size: 28px;
                color: #52c41a;
            }
        }

        .ready-text {
            font-size: 16px;
            font-weight: 600;
            color: #262626;
        }

        .ready-hint {
            font-size: 13px;
            color: #8c8c8c;
            margin-top: 6px;
        }
    }
}
</style>

